<template>
  <div class="hm-page">
    <div class="hm-hero-header">
      <section class="hm-hero">
        <AtsHeroBackground class="hm-hero__bg" :src="assets.heroBg" fallback-color="#eef2f6" />
        <div class="hm-hero__mask" aria-hidden="true" />
        <div class="hm-hero__inner">
          <div class="hm-hero__content">
            <h1 class="hm-hero__title">健康监测</h1>
            <p class="hm-hero__desc">
              依托“人工智能 + 大救星健康版App”，通过保险保障+智能设备+健康监测预警 系统+全球救援网络的大救星智能云平台为用户提供保险转嫁、健康监测、疾病 预警、医疗咨询、医疗救援、医疗费用直付/垫付、院后康复、找药送药、慢病 管理等综合服务，实现对用户全生命健康周期的管理。
            </p>
          </div>
        </div>
      </section>
    </div>

    <!-- 全生命周期健管服务 -->
    <section class="hm-section hm-section--loop" aria-label="全生命周期健管服务">
      <header class="hm-loop__head">
        <h2 class="hm-loop__title">「监测-预警-诊疗-救援-干预」全生命周期健管服务</h2>
      </header>
      <ul class="hm-loop__panels" role="list">
        <li
          v-for="panel in lifecyclePanels"
          :key="panel.title"
          class="hm-loop__panel"
          role="listitem"
        >
          <img
            class="hm-loop__panel-bg"
            :src="panel.bg"
            alt=""
            role="presentation"
            loading="lazy"
            decoding="async"
          >
          <div class="hm-loop__panel-mask" aria-hidden="true" />
          <p class="hm-loop__panel-text">{{ panel.title }}</p>
        </li>
      </ul>
    </section>

    <!-- 精准监测 -->
    <section
      class="hm-section hm-section--detect"
      :style="detectSectionStyle"
      aria-label="精准监测"
    >
      <div class="hm-detect">
        <h2 class="hm-detect__title">精准监测，智能守护</h2>
        <div class="hm-detect__main">
          <div class="hm-detect__media">
            <img
              class="hm-detect__img"
              :src="assets.sectionDetectBg"
              alt="精准监测产品展示"
              loading="lazy"
              decoding="async"
            >
          </div>
          <ul class="hm-detect__cards" role="list">
            <li
              v-for="item in detectFeatures"
              :key="item.title"
              class="hm-detect__item"
              role="listitem"
            >
              <h3 class="hm-detect__item-title">{{ item.title }}</h3>
              <p class="hm-detect__item-desc">{{ item.desc }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 趋势预报 / 风险预警 -->
    <section
      class="hm-section hm-section--warning"
      :style="warningSectionStyle"
      aria-label="趋势预报，风险预警"
    >
      <div class="hm-warning">
        <h2 class="hm-warning__title">趋势预报，风险预警</h2>
        <div class="hm-warning__main">
          <div class="hm-warning__left">
            <ul class="hm-warning__list" role="list">
              <li
                v-for="item in warningFeatures"
                :key="item.title"
                class="hm-warning__item"
                role="listitem"
              >
                <h3 class="hm-warning__item-title">{{ item.title }}</h3>
                <p class="hm-warning__item-desc">{{ item.desc }}</p>
              </li>
            </ul>
          </div>
          <div class="hm-warning__media">
            <img
              class="hm-warning__img"
              :src="assets.sectionStatsBgAlt"
              alt="趋势预报风险预警展示"
              loading="lazy"
              decoding="async"
            >
          </div>
        </div>
      </div>
    </section>

    <!-- 健康监测服务生态（结构对齐设计稿示意图） -->
    <section class="hm-section hm-section--ecosystem" aria-label="健康监测服务生态">
      <h2 class="hm-ecosystem__title">健康监测服务生态</h2>
      <div class="hm-ecosystem">
        <p class="hm-ecosystem__banner">{{ ecosystem.topBanner }}</p>
        <div class="hm-ecosystem__board">
          <div class="hm-ecosystem__anchor hm-ecosystem__anchor--left">{{ ecosystem.leftAnchor }}</div>
          <div class="hm-ecosystem__frame">
            <div class="hm-ecosystem__mode-ring" aria-hidden="true" />
            <p class="hm-ecosystem__channel hm-ecosystem__frame-pos hm-ecosystem__frame-pos--channel">{{ ecosystem.topChannel }}</p>
            <span class="hm-ecosystem__mode hm-ecosystem__frame-pos hm-ecosystem__frame-pos--o2o">{{ ecosystem.modes.o2o }}</span>
            <span class="hm-ecosystem__mode hm-ecosystem__frame-pos hm-ecosystem__frame-pos--offline">{{ ecosystem.modes.offline }}</span>
            <span class="hm-ecosystem__mode hm-ecosystem__mode--vertical hm-ecosystem__frame-pos hm-ecosystem__frame-pos--b2c">{{ ecosystem.modes.b2c }}</span>
            <span class="hm-ecosystem__mode hm-ecosystem__mode--vertical hm-ecosystem__frame-pos hm-ecosystem__frame-pos--platform">{{ ecosystem.modes.platform }}</span>
            <span class="hm-ecosystem__mode hm-ecosystem__frame-pos hm-ecosystem__frame-pos--b2b">{{ ecosystem.modes.b2b }}</span>
            <span class="hm-ecosystem__mode hm-ecosystem__frame-pos hm-ecosystem__frame-pos--bigdata">{{ ecosystem.modes.bigData }}</span>

            <div class="hm-ecosystem__matrix">
              <div class="hm-ecosystem__pill-col hm-ecosystem__pill-col--before">
                <span class="hm-ecosystem__pill hm-ecosystem__pill--top">{{ ecosystem.pills.trend }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--mid">{{ ecosystem.pills.pain }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--bottom">{{ ecosystem.pills.report }}</span>
              </div>
              <span class="hm-ecosystem__core hm-ecosystem__core--round">{{ ecosystem.leftCore }}</span>
              <div class="hm-ecosystem__pill-col hm-ecosystem__pill-col--after">
                <span class="hm-ecosystem__pill hm-ecosystem__pill--top">{{ ecosystem.pills.warn }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--mid">{{ ecosystem.pills.doctor }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--bottom">{{ ecosystem.pills.team }}</span>
              </div>
              <span class="hm-ecosystem__client">{{ ecosystem.center }}</span>
              <div class="hm-ecosystem__pill-col hm-ecosystem__pill-col--before">
                <span class="hm-ecosystem__pill hm-ecosystem__pill--top">{{ ecosystem.pills.monitor }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--mid">{{ ecosystem.pills.delivery }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--bottom">{{ ecosystem.pills.globalDrug }}</span>
              </div>
              <span class="hm-ecosystem__core hm-ecosystem__core--round hm-ecosystem__core--loop">
                <span class="hm-ecosystem__core-text">{{ ecosystem.rightCoreLine1 }}</span>
                <span class="hm-ecosystem__core-text">{{ ecosystem.rightCoreLine2 }}</span>
              </span>
              <div class="hm-ecosystem__pill-col hm-ecosystem__pill-col--after">
                <span class="hm-ecosystem__pill hm-ecosystem__pill--top">{{ ecosystem.pills.consult }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--mid">{{ ecosystem.pills.resource }}</span>
                <span class="hm-ecosystem__pill hm-ecosystem__pill--bottom">{{ ecosystem.pills.smart }}</span>
              </div>
            </div>
          </div>
          <div class="hm-ecosystem__anchor hm-ecosystem__anchor--right">{{ ecosystem.rightAnchor }}</div>
        </div>
      </div>
    </section>

    <!-- 服务资源 -->
    <section class="hm-section hm-section--stats" :style="statsSectionStyle" aria-label="服务资源">
      <header class="hm-stats__head">
        <h2 class="hm-stats__title">服务资源</h2>
        <span class="hm-stats__rule" aria-hidden="true" />
        <p class="hm-stats__subtitle">
          与各大医院、健康管理公司、体检机构、眼科机构、齿科机构、药店等深度合作，实现全方位的健康服务网络
        </p>
      </header>
      <div class="hm-stats__grid" role="list">
        <div
          v-for="(col, colIndex) in statsColumns"
          :key="`stats-col-${colIndex}`"
          class="hm-stats__col"
          :class="{
            'hm-stats__col--double': col.items.length > 1,
            'hm-stats__col--single-top': col.items.length === 1
          }"
          role="listitem"
        >
          <article
            v-for="item in col.items"
            :key="`${item.number}-${item.label}`"
            class="hm-stats__block"
          >
            <p class="hm-stats__value">
              <strong class="hm-stats__num">{{ item.number }}</strong>
              <span v-if="item.label" class="hm-stats__label">{{ item.label }}</span>
            </p>
            <p v-if="item.desc" class="hm-stats__desc">{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- 持续追踪需求 -->
    <section class="hm-section hm-section--match" aria-label="持续追踪需求">
      <h2 class="hm-match__title">持续追踪需求·专业守护健康</h2>
      <ul class="hm-match">
        <li v-for="item in matchCards" :key="item.title" class="hm-match__item">
          <span class="hm-match__icon-wrap">
            <img
              class="hm-match__icon"
              :src="item.icon"
              alt=""
              role="presentation"
              decoding="async"
            >
          </span>
          <h3 class="hm-match__item-title">{{ item.title }}</h3>
          <p class="hm-match__item-sub">{{ item.subtitle }}</p>
        </li>
      </ul>
    </section>

    <!-- CTA -->
    <section class="hm-section hm-section--cta" aria-label="主动守护">
      <img
        class="hm-cta__bg"
        :src="assets.sectionCtaBanner"
        alt=""
        role="presentation"
        decoding="async"
      >
      <div class="hm-cta__mask" aria-hidden="true" />
      <div class="hm-cta__inner">
        <h2 class="hm-cta__title">从「被动记录」到「主动守护」</h2>
        <p class="hm-cta__desc">构建心血管疾病防控全闭环服务体系</p>
      </div>
    </section>

    <AtsSiteFooter />
  </div>
</template>

<script>
import AtsSiteFooter from '@/components/ats/AtsSiteFooter.vue'
import AtsHeroBackground from '@/components/ats/AtsHeroBackground.vue'

/**
 * 健康监测专题页：素材目录 `src/assets/healthMonitoring`，统一 hm_{模块}_{用途}.png 命名
 */
export default {
  name: 'HealthMonitoringPage',
  components: {
    AtsSiteFooter,
    AtsHeroBackground
  },
  data () {
    return {
      /** 服务生态示意图文案（对齐设计稿） */
      ecosystem: {
        topBanner: '健康监测设备配套服务管理解决方案',
        leftAnchor: '健康监测设备',
        rightAnchor: '健康管理服务',
        topChannel: '通过线上和线下全渠道业务运营布局',
        center: '客户',
        leftCore: '健康档案',
        rightCoreLine1: '健康管理',
        rightCoreLine2: '服务闭环',
        pills: {
          trend: '趋势预报',
          pain: '发现痛点',
          report: '健康报告',
          warn: '及时预警',
          doctor: '私人医生',
          team: '服务团队',
          globalDrug: '全球找药',
          monitor: '健康监测',
          delivery: '医疗送返',
          consult: '在线问诊',
          resource: '医疗资源',
          smart: '数据挖掘'
        },
        modes: {
          o2o: 'O2O模式',
          offline: '线下体验',
          b2c: 'B2C模式',
          b2b: 'B2B模式',
          platform: '第三方平台',
          bigData: '大数据平台'
        }
      },
      assets: {
        heroBg: require('@/assets/healthMonitoring/hm_hero_bg.png'),
        loopCard01: require('@/assets/healthMonitoring/hm_loop_card_01.png'),
        loopCard02: require('@/assets/healthMonitoring/hm_loop_card_02.png'),
        loopCard03: require('@/assets/healthMonitoring/hm_loop_card_03.png'),
        loopCard04: require('@/assets/healthMonitoring/hm_loop_card_04.png'),
        loopCard05: require('@/assets/healthMonitoring/hm_loop_card_05.png'),
        trendForecastBg: require('@/assets/healthMonitoring/trendforecast.png'),
        sectionDetectBg: require('@/assets/healthMonitoring/hm_section_detect_bg.png'),
        sectionStatsBg: require('@/assets/healthMonitoring/hm_section_stats_bg.png'),
        serviceResources: require('@/assets/healthMonitoring/serviceResources.png'),
        sectionStatsBgAlt: require('@/assets/healthMonitoring/hm_section_stats_bg_alt.png'),
        sectionCtaBanner: require('@/assets/healthMonitoring/hm_section_cta_banner.png'),
        iconMatch01: require('@/assets/healthMonitoring/hm_icon_match_01.png'),
        iconMatch02: require('@/assets/healthMonitoring/hm_icon_match_02.png'),
        iconMatch03: require('@/assets/healthMonitoring/hm_icon_match_03.png')
      }
    }
  },
  computed: {
    /**
     * 全生命周期五栏（左→右）
     * @returns {Array<{ title: string, bg: string }>}
     */
    lifecyclePanels () {
      const a = this.assets
      return [
        { title: '持续精准检监测', bg: a.loopCard05 },
        { title: '智能异常预警', bg: a.loopCard04 },
        { title: '健康干预指导', bg: a.loopCard03 },
        { title: '三级联动急救', bg: a.loopCard02 },
        { title: '全球救援覆盖', bg: a.loopCard01 }
      ]
    },
    /**
     * 精准监测特性列表
     * @returns {Array<{ title: string, desc: string }>}
     */
    detectFeatures () {
      return [
        {
          title: '动态心电仪',
          desc:
            '采用最新高速无线传输技术及智能心电分析技术，检测过程中随时可在手机上显示房颤、心律不齐、早搏等16项检测结果。方便用户「随测随看」有问题及时预警，为心脏病的早发现、早治疗赢得宝贵时间。'
        },
        {
          title: '智能监测手表',
          desc:
            '24小时健康监测，包括心率、血氧、睡眠等关键指标，支持预警和语音交互功能，「一键救援」功能在紧急情况下迅速响应，为用户提供高效安全保障。'
        },
        {
          title: '医疗级血压计',
          desc:
            '大屏清晰，一键操作，适合日常监测。智能加压误动提醒助力健康管理。通过「大救星健康版」APP绑定设备后，测量数据会同步在个人健康档案中。'
        }
      ]
    },
    /**
     * 精准监测区背景（hm_section_warning_app）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    detectSectionStyle () {
      return {
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 趋势预报区背景（trendforecast）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    warningSectionStyle () {
      return {
        backgroundColor: '#eef1f4',
        backgroundImage: `url(${this.assets.trendForecastBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 风险预警特性列表
     * @returns {Array<{ title: string, desc: string }>}
     */
    warningFeatures () {
      return [
        {
          title: '疾病风险预警服务',
          desc:
            '实时掌握自己的生命体征。一旦突发健康数据异常，手表会及时发出提醒，提醒用户关注异常信息并采取必要的处置措施。'
        },
        {
          title: '健康监测日报/周报',
          desc:
            '全天候监测心电、心率、血压、血氧等健康数据。结合医疗器械测量结果，依托人工智能进行专业解读和疾病风险预警。自动生成个性化的健康日报/周报，全面评估身体健康状况，帮助用户在早期识别潜在健康问题，并给出针对性的干预方案。'
        },
        {
          title: '动态心电仪',
          desc:
            '依托中国中医科学院科研成果——中医五藏态势监测技术及科学中医诊断，每隔24小时系统生成中医报告，显示五藏功能状态：心藏、肝藏、肾藏、脾藏评分结果并可查看其它体测数据。'
        }
      ]
    },
    /**
     * 服务资源统计（四列错落排版，对齐设计稿）
     * @returns {Array<{ items: Array<{ number: string, label: string, desc: string }> }>}
     */
    statsColumns () {
      return [
        {
          items: [
            { number: '1000', label: '余家三甲医院', desc: '绿通服务覆盖全国' },
            { number: '333', label: '座城市', desc: '合作国内顶级心、脑、癌专科名院可覆盖全国' }
          ]
        },
        {
          items: [
            { number: '333', label: '个城市', desc: '构建全国护士网络' }
          ]
        },
        {
          items: [
            { number: '30,000+', label: '线下药店', desc: '医药网络' },
            { number: '100', label: '座城市', desc: '覆盖城市' }
          ]
        },
        {
          items: [
            { number: '3w+', label: '', desc: '全科医生+专科医生+名医+专家组成' }
          ]
        }
      ]
    },
    /**
     * 精准匹配需求三栏
     * @returns {Array<{ title: string, subtitle: string, icon: string }>}
     */
    matchCards () {
      const a = this.assets
      return [
        { title: '个性化方案', subtitle: '精准匹配·科学干预', icon: a.iconMatch01 },
        { title: '专业团队', subtitle: '名医专家·全程守护', icon: a.iconMatch02 },
        { title: '持续追踪', subtitle: '动态监测·主动管理', icon: a.iconMatch03 }
      ]
    },
    /**
     * 服务资源区背景（世界地图）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    statsSectionStyle () {
      return {
        backgroundColor: '#0a1628',
        backgroundImage: `url(${this.assets.serviceResources})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat'
      }
    }
  },
  methods: {
    /**
     * @param {{ id: string }} item
     * @returns {void}
     */
  }
}
</script>

<style lang="scss" scoped>
$green: #256a30;
$text-dark: #1a2226;
$navy: #152443;

.hm-page {
  min-height: 100vh;
  background: #ffffff;
  color: $text-dark;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.hm-hero-header {
  position: relative;
  height: 871px;
  min-height: 871px;
  max-height: 871px;
}

.hm-hero {
  position: relative;
  height: 100%;
  min-height: 871px;
  overflow: hidden;
  box-sizing: border-box;
}

.hm-hero__bg {
  background-size: cover;
  background-position: center bottom;
}

.hm-hero__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.06) 0%,
    rgba(0, 0, 0, 0) 42%,
    rgba(0, 0, 0, 0.12) 100%
  );
  pointer-events: none;
}

.hm-hero__inner {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  height: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 36px;
  box-sizing: border-box;
}

.hm-hero__content {
  max-width: 982px;
  color: #ffffff;
  text-align: left;
}

.hm-hero__title {
  margin: 0 0 50px;
  color: #ffffff;
  font-size: 56px;
  font-weight: 800;
  line-height: 1.22;
  letter-spacing: 0.08em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5), 0 4px 28px rgba(0, 0, 0, 0.4);
}

.hm-hero__desc {
  margin: 0;
  color: #ffffff;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.75;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45), 0 2px 16px rgba(0, 0, 0, 0.35);
  max-width: 831px;
}

.hm-section {
  margin: 0 auto;
  padding: 80px max(24px, calc((100% - 1320px) / 2));
  box-sizing: border-box;
}

.hm-h2 {
  margin: 0 0 48px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: $text-dark;
  text-align: center;
}

.hm-h2--on-dark {
  color: #ffffff;
}

/* 全生命周期健康服务 */
.hm-section--loop {
  background: #ffffff;
  box-sizing: border-box;
  padding-top: 120px;
  padding-bottom: 88px;
}

.hm-loop__head {
  max-width: 1320px;
  margin: 0 auto 56px;
  text-align: center;
}

.hm-loop__title {
  margin: 0;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.35;
  color: #1a2226;
  letter-spacing: 0.02em;
}

.hm-loop__panels {
  list-style: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0;
  min-height: 420px;
}

.hm-loop__panel {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 420px;
  overflow: hidden;
}

.hm-loop__panel-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.hm-loop__panel-mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 20, 40, 0.15) 0%,
    rgba(0, 20, 40, 0.45) 100%
  );
  pointer-events: none;
}

.hm-loop__panel-text {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 420px;
  margin: 0;
  padding: 24px 12px;
  box-sizing: border-box;
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.45;
  text-align: center;
  letter-spacing: 0.06em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.55);
}

/* 精准监测 */
.hm-section--detect {
  padding-top: 88px;
  padding-bottom: 88px;
}

.hm-detect {
  max-width: 1320px;
  margin: 0 auto;
}

.hm-detect__title {
  margin: 0 0 56px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: #1a2226;
  text-align: left;
}

/** 左图 + 右侧三卡横排 */
.hm-detect__main {
  display: flex;
  align-items: stretch;
  gap: 28px;
}

.hm-detect__media {
  flex: 0 0 456px;
  width: 456px;
  max-width: 456px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hm-detect__img {
  display: block;
  width: 456px;
  height: 568px;
  max-width: 100%;
  object-fit: contain;
  object-position: center;
}

.hm-detect__cards {
  list-style: none;
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  margin-top: 95px;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px;
}

.hm-detect__item {
  flex: 0 0 260px;
  width: 260px;
  height: 375px;
  padding: 36px 22px 32px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 6px 28px rgba(15, 35, 52, 0.1);
  overflow: hidden;
}

.hm-detect__item-title {
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
  color: $navy;
  text-align: center;
}

.hm-detect__item-desc {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.8;
  color: #5a6472;
  text-align: left;
}

/* 趋势预报 / 风险预警：左栏标题+三卡，右栏 App 拼图 */
.hm-section--warning {
  position: relative;
  height: 1122px;
  min-height: 1122px;
  max-height: 1122px;
  box-sizing: border-box;
  padding: 0 max(24px, calc((100% - 1320px) / 2));
  overflow: hidden;
}

.hm-warning {
  position: relative;
  max-width: 1320px;
  height: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.hm-warning__title {
  position: relative;
  z-index: 2;
  margin: 120px 0 70px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: #1a2226;
  text-align: left;
}

.hm-warning__main {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 120px;
}

.hm-warning__left {
  position: relative;
  z-index: 2;
  flex: 0 0 500px;
  width: 500px;
  min-width: 0;
}

.hm-warning__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.hm-warning__item {
  width: 100%;
  height: 232px;
  padding: 40px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 6px 28px rgba(15, 35, 52, 0.1);
  overflow: hidden;
}

.hm-warning__item-title {
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  color: $navy;
  text-align: left;
}

.hm-warning__item-desc {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.75;
  color: #5a6472;
  text-align: left;
}

.hm-warning__media {
  position: absolute;
  top: -122px;
  left: 545px;
  z-index: 1;
  flex: none;
  width: 1160px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  margin: 0;
  pointer-events: none;
}

.hm-warning__media .hm-warning__img {
  pointer-events: auto;
}

.hm-warning__img {
  display: block;
  width: 1160px;
  height: 1122px;
  object-fit: contain;
  object-position: center top;
}

/* 健康监测服务生态 */
.hm-section--ecosystem {
  padding-top: 120px;
  padding-bottom: 120px;
  background: #ffffff;
}

.hm-ecosystem__title {
  margin: 0 0 80px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: $text-dark;
  text-align: center;
}

.hm-ecosystem {
  max-width: 1600px;
  margin: 0 auto;
}

.hm-ecosystem__banner {
  position: relative;
  z-index: 3;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto -28px;
  padding: 14px 36px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: #ffffff;
  text-align: center;
  background: $green;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(37, 106, 48, 0.22);
}

/** 虚线外框：左右绿卡片中心压在边框竖线上 */
.hm-ecosystem__board {
  position: relative;
  overflow: visible;
  box-sizing: border-box;
  padding: 48px 40px 44px;
  border: 2px dashed #256a30;
  border-radius: 32px;
  background: #ffffff;
}

.hm-ecosystem__anchor {
  position: absolute;
  top: 50%;
  z-index: 2;
  min-width: 132px;
  padding: 18px 28px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
  text-align: center;
  background: $green;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(37, 106, 48, 0.22);
  transform: translateY(-50%);
}

.hm-ecosystem__anchor--left {
  left: 0;
  transform: translate(-50%, -50%);
}

.hm-ecosystem__anchor--right {
  right: 0;
  transform: translate(50%, -50%);
}

.hm-ecosystem__frame {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1050px;
  height: 350px;
  margin: 20px auto 0;
  box-sizing: border-box;
}

/** 中部绿色虚线框，灰色标签压在边框上 */
.hm-ecosystem__mode-ring {
  position: absolute;
  inset: 22px 20px 20px;
  z-index: 0;
  border: 2px dashed #256a30;
  border-radius: 8px;
  pointer-events: none;
}

.hm-ecosystem__frame-pos {
  position: absolute;
  z-index: 3;
  margin: 0;
  transform: translate(-50%, -50%);
}

.hm-ecosystem__channel {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 240px;
  height: 90px;
  padding: 8px 12px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.45;
  color: #ffffff;
  text-align: center;
  background: #999999;
  border-radius: 8px;
}

.hm-ecosystem__mode {
  padding: 10px 22px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  color: #ffffff;
  white-space: nowrap;
  background: #999999;
  border-radius: 8px;
}

.hm-ecosystem__mode--vertical {
  padding: 22px 10px;
  line-height: 1.2;
  letter-spacing: 0.12em;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  white-space: nowrap;
}

.hm-ecosystem__frame-pos--channel { top: 22px; left: 50%; }
.hm-ecosystem__frame-pos--o2o { top: 22px; left: 20%; }
.hm-ecosystem__frame-pos--offline { top: 22px; left: 80%; }
.hm-ecosystem__frame-pos--b2c { top: 50%; left: 20px; }
.hm-ecosystem__frame-pos--platform { top: 50%; left: calc(100% - 20px); }
.hm-ecosystem__frame-pos--b2b { top: calc(100% - 20px); left: 20%; }
.hm-ecosystem__frame-pos--bigdata { top: calc(100% - 20px); left: 80%; }

/** 四列胶囊 + 三圆横向排列 */
.hm-ecosystem__matrix {
  position: absolute;
  top: 56px;
  right: 52px;
  bottom: 38px;
  left: 52px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/**
 * 胶囊列：上/中/下错位排版，中间项与相邻圆心垂直对齐（对齐设计稿）
 */
.hm-ecosystem__pill-col {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  width: 110px;
  min-height: 128px;
  flex-shrink: 0;
}

/** 圆心在列右侧：胶囊靠右，上提/下压形成弧度 */
.hm-ecosystem__pill-col--before .hm-ecosystem__pill--top {
  grid-row: 1;
  justify-self: end;
  align-self: end;
  transform: translate(18px, -25px);
}

.hm-ecosystem__pill-col--before .hm-ecosystem__pill--mid {
  grid-row: 2;
  justify-self: end;
}

.hm-ecosystem__pill-col--before .hm-ecosystem__pill--bottom {
  grid-row: 3;
  justify-self: end;
  align-self: start;
  transform: translate(18px, 25px);
}

/** 圆心在列左侧：胶囊靠左，镜像弧度 */
.hm-ecosystem__pill-col--after .hm-ecosystem__pill--top {
  grid-row: 1;
  justify-self: start;
  align-self: end;
  transform: translate(-18px, -25px);
}

.hm-ecosystem__pill-col--after .hm-ecosystem__pill--mid {
  grid-row: 2;
  justify-self: start;
}

.hm-ecosystem__pill-col--after .hm-ecosystem__pill--bottom {
  grid-row: 3;
  justify-self: start;
  align-self: start;
  transform: translate(-6px, 25px);
}

.hm-ecosystem__matrix .hm-ecosystem__core--round,
.hm-ecosystem__matrix .hm-ecosystem__client {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.hm-ecosystem__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 110px;
  height: 40px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  color: $green;
  text-align: center;
  white-space: nowrap;
  background: #ffffff;
  border: 1px solid $green;
  border-radius: 999px;
}

.hm-ecosystem__core--round {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 90px;
  height: 90px;
  padding: 6px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
  text-align: center;
  background: $green;
  border: none;
  border-radius: 50%;
  box-shadow: 0 6px 20px rgba(37, 106, 48, 0.28);
}

.hm-ecosystem__core--loop {
  flex-direction: column;
  gap: 2px;
  line-height: 1.2;
  color: #ffffff;
}

.hm-ecosystem__core-text {
  display: block;
  font-size: 17px;
  line-height: 1.2;
  color: #ffffff;
  white-space: nowrap;
}

.hm-ecosystem__client {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 115px;
  height: 115px;
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  background: $green;
  border-radius: 50%;
  box-shadow: 0 6px 20px rgba(37, 106, 48, 0.28);
}

/* 服务资源（背景 serviceResources，四列错落数据） */
.hm-section--stats {
  max-width: none;
  padding: 72px max(24px, calc((100% - 1320px) / 2)) 88px;
  color: #ffffff;
  box-sizing: border-box;
}

.hm-stats__head {
  max-width: 900px;
  margin: 0 auto 64px;
  text-align: center;
}

.hm-stats__title {
  margin: 0 0 20px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: #ffffff;
  letter-spacing: 0.06em;
}

.hm-stats__rule {
  display: block;
  width: 56px;
  height: 3px;
  margin: 0 auto 24px;
  background: #ffffff;
}

.hm-stats__subtitle {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.02em;
}

.hm-stats__grid {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  box-sizing: border-box;
}

.hm-stats__col {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-width: 0;
  margin-right: 100px;
  text-align: left;
}

.hm-stats__col:last-child {
  margin-right: 0;
}

.hm-stats__col--double {
  justify-content: space-between;
  min-height: 280px;
}

.hm-stats__col:nth-child(1) {
  flex: 0 1 30%;
  max-width: 340px;
}

.hm-stats__col:nth-child(2) {
  flex: 0 0 auto;
}

.hm-stats__col:nth-child(3) {
  flex: 0 1 22%;
  max-width: 260px;
}

.hm-stats__col:nth-child(4) {
  flex: 0 1 26%;
  max-width: 300px;
}

.hm-stats__col--single-top {
  justify-content: flex-start;
  min-height: 0;
  padding-top: 0;
}

.hm-stats__block {
  margin: 0;
  max-width: 100%;
}

.hm-stats__value {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 8px;
  margin: 0 0 12px;
  line-height: 1.35;
}

.hm-stats__num {
  font-size: 36px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.hm-stats__label {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.hm-stats__desc {
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
}

/* 精准匹配需求 */
.hm-section--match {
  padding-top: 88px;
  padding-bottom: 88px;
  background: #ffffff;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.hm-match__title {
  margin: 0 auto 72px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: #1a2226;
  text-align: center;
  letter-spacing: 0.02em;
}

.hm-match {
  list-style: none;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  gap: 363px;
  width: max-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

.hm-match__item {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  width: 280px;
  margin: 0;
  text-align: center;
}

.hm-match__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  background: #e8f6ef;
}

.hm-match__icon {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.hm-match__item-title {
  margin: 28px 0 12px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a2226;
}

.hm-match__item-sub {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: #6b7280;
  letter-spacing: 0.02em;
}

/* CTA */
.hm-section.hm-section--cta {
  position: relative;
  margin: 0;
  padding: 0;
  max-width: none;
  min-height: 382px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.hm-cta__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  object-position: center;
  display: block;
}

.hm-cta__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 24, 48, 0.52) 0%,
    rgba(0, 32, 64, 0.58) 100%
  );
  pointer-events: none;
}

.hm-cta__inner {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  box-sizing: border-box;
  text-align: center;
}

.hm-cta__title {
  margin: 0 0 24px;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
  letter-spacing: 0.04em;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.4);
}

.hm-cta__desc {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.35);
}

@media (max-width: 1100px) {
  .hm-detect__main {
    flex-direction: column;
    align-items: stretch;
    gap: 40px;
  }
  .hm-detect__media {
    flex: none;
    width: 100%;
    max-width: 100%;
  }
  .hm-detect__img {
    width: 100%;
    height: auto;
    max-width: 456px;
    aspect-ratio: 456 / 568;
  }
  .hm-detect__cards {
    flex-direction: column;
    gap: 24px;
  }
  .hm-detect__item {
    flex: none;
    width: 100%;
    max-width: 260px;
    height: auto;
    min-height: 375px;
  }
  .hm-section--warning {
    height: auto;
    min-height: 0;
    max-height: none;
    overflow: visible;
    padding: 48px 24px;
  }
  .hm-warning__title {
    margin: 64px 0 48px;
  }
  .hm-warning__main {
    flex-direction: column;
    align-items: stretch;
    gap: 40px;
  }
  .hm-warning__left {
    flex: none;
    width: 100%;
    max-width: 500px;
  }
  .hm-warning__item {
    height: auto;
    min-height: 232px;
    overflow: visible;
  }
  .hm-warning__media {
    position: static;
    left: auto;
    width: 100%;
    margin: 0;
    padding: 0;
    pointer-events: auto;
  }
  .hm-warning__img {
    width: 100%;
    height: auto;
  }
  .hm-stats__grid {
    flex-wrap: wrap;
    justify-content: space-between;
    row-gap: 48px;
  }
  .hm-stats__col {
    flex: 0 0 calc(50% - 16px);
    max-width: calc(50% - 16px);
    min-height: 0;
    margin-right: 0;
  }
  .hm-stats__col:nth-child(odd) {
    margin-right: 48px;
  }
  .hm-stats__col:nth-child(even) {
    margin-right: 0;
  }
  .hm-stats__col--double {
    min-height: 0;
    gap: 32px;
  }
  .hm-stats__col--single-top {
    justify-content: flex-start;
  }
  .hm-stats__block {
    max-width: none;
  }
  .hm-stats__num {
    font-size: 28px;
  }
  .hm-stats__label {
    font-size: 18px;
  }
  .hm-match {
    flex-wrap: wrap;
    gap: 48px;
    justify-content: center;
    overflow-x: visible;
  }
  .hm-match__item {
    flex-shrink: 0;
    width: min(280px, calc(33.333% - 24px));
    max-width: 280px;
  }
}

@media (max-width: 720px) {
  .hm-loop__panel {
    flex: 1 1 100%;
    min-height: 220px;
  }
  .hm-loop__panel-text {
    min-height: 220px;
    font-size: 17px;
  }
}

@media (max-width: 960px) {
  .hm-hero__desc {
    max-width: 100%;
    font-size: 16px;
  }
  .hm-hero-header {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .hm-hero {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .hm-h2 {
    font-size: 32px;
  }
  .hm-loop__title {
    font-size: 28px;
  }
  .hm-loop__panels {
    flex-wrap: wrap;
    min-height: 0;
  }
  .hm-loop__panel {
    flex: 1 1 50%;
    min-height: 280px;
  }
  .hm-loop__panel-text {
    min-height: 280px;
    font-size: 18px;
  }
  .hm-detect__title {
    font-size: 32px;
  }
  .hm-warning__title {
    font-size: 32px;
  }
  .hm-match__title {
    font-size: 32px;
    margin-bottom: 48px;
  }
  .hm-match__item-title {
    font-size: 24px;
  }
  .hm-match__item-sub {
    font-size: 16px;
  }
  .hm-section--stats {
    padding: 48px 24px 56px;
  }
  .hm-stats__head {
    margin-bottom: 40px;
  }
  .hm-stats__title {
    font-size: 32px;
  }
  .hm-stats__subtitle {
    font-size: 16px;
  }
  .hm-stats__grid {
    flex-direction: column;
    align-items: stretch;
    min-height: 0;
    gap: 32px;
  }
  .hm-stats__col {
    flex: 1 1 auto;
    max-width: 100%;
    min-height: 0;
    margin-right: 0;
  }
  .hm-stats__col--double {
    gap: 28px;
  }
  .hm-stats__col--single-top {
    justify-content: flex-start;
  }
  .hm-section--ecosystem {
    padding-top: 64px;
    padding-bottom: 64px;
  }
  .hm-ecosystem__title {
    margin-bottom: 48px;
    font-size: 32px;
  }
  .hm-ecosystem__banner {
    margin-bottom: -20px;
    padding: 12px 20px;
    font-size: 20px;
  }
  .hm-ecosystem__board {
    padding: 40px 20px 32px;
  }
  .hm-ecosystem__anchor {
    position: static;
    width: 100%;
    max-width: 320px;
    min-width: 0;
    margin: 0 auto 16px;
    transform: none;
  }
  .hm-ecosystem__anchor--right {
    margin: 16px auto 0;
  }
  .hm-ecosystem__frame {
    height: auto;
    min-height: 0;
    padding-bottom: 24px;
  }
  .hm-ecosystem__mode-ring {
    display: none;
  }
  .hm-ecosystem__frame-pos {
    position: relative;
    top: auto !important;
    left: auto !important;
    transform: none;
    display: flex;
    justify-content: center;
    margin: 8px auto;
  }
  .hm-ecosystem__matrix {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
    padding: 0 12px;
  }
  .hm-ecosystem__pill-col {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    min-height: 0;
  }
  .hm-ecosystem__pill-col .hm-ecosystem__pill--top,
  .hm-ecosystem__pill-col .hm-ecosystem__pill--mid,
  .hm-ecosystem__pill-col .hm-ecosystem__pill--bottom {
    transform: none;
  }
  .hm-ecosystem__pill {
    width: 110px;
    height: 40px;
    font-size: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/healthMonitoring/healthMonitoring.vue